import React, { useContext, useEffect, useState, useRef, useCallback, Fragment } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { format } from 'date-fns'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { IoIosArrowForward } from 'react-icons/io'
import { GrDownload } from 'react-icons/gr'
import { FaXTwitter, FaTiktok, FaTelegram, FaDiscord, FaSnapchat, FaSquareXTwitter } from 'react-icons/fa6'
import { FaTelegramPlane, FaSnapchatGhost } from 'react-icons/fa'
import CompetitionSelector from '../components/CompetitionSelector'
import { Link } from 'react-router-dom'
import { AuthContext } from '../contexts/AuthContext'
import { FaExternalLinkAlt } from 'react-icons/fa'
import ReportPdfDialog from '../components/ReportPdfDialog'

const Report = () => {
  const {
    getreportmatchdays,
    getoverviewdaymatches,
    getoverviewdaywebsites,
    getoverviewmatchdaydata,
    getoverviewmatchdaydatatotals,
    getCompetitions,
    isLoading,
  } = useContext(AppContext)
  const { isAdmin } = useContext(AuthContext)
  const [competitions, setCompetitions] = useState([])
  const [slctdCompetition, setslctdCompetition] = useState('')
  const [matchdays, setMatchdays] = useState([])
  const [matchday, setMatchday] = useState()
  const [matches, setMatches] = useState([])
  const [websites, setWebsites] = useState([])
  const [matchdayData, setMatchdayData] = useState([])
  const [matchdayDataTotals, setMatchdayDataTotals] = useState([])
  const [matchdayDataSpectators, setMatchdayDataSpectators] = useState(0)
  const [highlightWebsitesTotal, setHighlightWebsitesTotal] = useState(0)
  const [liveWebsitesTotal, setLiveWebsitesTotal] = useState(0)
  const [reportLoading, setReportLoading] = useState(false)
  const requestRef = useRef(0)
  const [ddlTotal, setDdlTotal] = useState(0)
  const [showPdfDialog, setShowPdfDialog] = useState(false)

  const fetchMatchDays = useCallback(async () => {
    const response = await getreportmatchdays(slctdCompetition?.id)
    setMatchdays(response.matchdays)
  }, [getreportmatchdays, slctdCompetition])

  const fetchMatchDaysMatches = useCallback(async () => {
    const response = await getoverviewdaymatches(matchday, slctdCompetition?.id)
    setMatches(response)
  }, [getoverviewdaymatches, matchday, slctdCompetition])

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response)
    if (response.length > 0) {
      setslctdCompetition(response[0])
    }
  }, [getCompetitions])

  const fetchMatchDayWebsites = useCallback(async () => {
    setReportLoading(true)
    const [websitesResponse, totalsResponse] = await Promise.all([
      getoverviewdaywebsites(matchday, slctdCompetition.id),
      getoverviewmatchdaydatatotals(matchday, slctdCompetition.id),
    ])

    setWebsites(websitesResponse)

    // the total response is wrong here

    const template = Object.keys(totalsResponse?.matchLinksTotals?.[0] || {})

    const dataTotals = {
      totalgooglelinks: 0,
      totalhighlightlinks: 0,
      totallinks: 0,
      totallivelinks: 0,
    }

    totalsResponse.matchLinksTotals.forEach((record) => {
      dataTotals.totalgooglelinks += record.totalgooglelinks
      dataTotals.totalhighlightlinks += record.totalhighlightlinks
      dataTotals.totallinks += record.totallinks
      dataTotals.totallivelinks += record.totallivelinks
    })

    setMatchdayDataTotals(dataTotals)

    const totalHighlightLinks = websitesResponse.reduce((sum, website) => sum + (website.highlightlinks || 0), 0)
    setHighlightWebsitesTotal(totalHighlightLinks)

    const updatedWebsites = websitesResponse.map((item) => {
      if (item.name === 'DDL') {
        setDdlTotal(item.totallinks)
      }
      if (item.name === 'Google' || item.name === 'Bing') {
        item.livelinks = dataTotals.totalgooglelinks
        item.highlightlinks = Math.max(0, dataTotals.totalhighlightlinks - totalHighlightLinks)
      }
      return item
    })
    setWebsites(updatedWebsites)

    setMatchdayDataSpectators(totalsResponse.totalSpectators)
    setDdlTotal(totalsResponse.totalddl)
    setReportLoading(false)
  }, [getoverviewdaywebsites, getoverviewmatchdaydatatotals, slctdCompetition, matchday])

  const fetchMatchDayData = useCallback(async () => {
    const response = await getoverviewmatchdaydata(matchday, slctdCompetition.id)
    setMatchdayData(response[0])
  }, [getoverviewmatchdaydata, matchday, slctdCompetition])

  const onClickMatchDay = (day) => {
    const url = new URL(window.location.href)

    // If we're closing the current report, remove the URL parameter
    if (matchday === day.matchday) {
      url.searchParams.delete('report')
    } else {
      url.searchParams.set('report', day.matchday)
    }

    window.history.pushState(null, null, url)

    // Toggle the matchday - if it's already selected, close it, otherwise open it
    setMatchday((prevMatchday) => (prevMatchday === day.matchday ? '' : day.matchday))
  }

  const sortMatchdays = (matchdays) => {
    if (!matchdays || matchdays.length === 0) return matchdays

    const getSortOrder = (matchDay) => {
      const matchDayStr = String(matchDay).toUpperCase().trim()

      // Check if it's a pure numeric matchday (1, 2, 3, etc.)
      // Must be exactly a number (no extra characters)
      if (/^\d+$/.test(matchDayStr)) {
        const num = parseInt(matchDayStr)
        // Return negative value so numeric matchdays come first
        return -num
      }

      // Special rounds - these come after numeric and other matchdays
      // Order: Round of 16, Round of 8, Semi Finals, 3rd Place, Final

      // Round of 16
      if (
        matchDayStr.includes('ROUND OF 16') ||
        matchDayStr.includes('ROUND 16') ||
        matchDayStr.includes('ROUND16') ||
        matchDayStr.includes('ROUNDOF16')
      ) {
        return 10000
      }

      // Round of 8
      if (
        matchDayStr.includes('ROUND OF 8') ||
        matchDayStr.includes('ROUND 8') ||
        matchDayStr.includes('ROUND8') ||
        matchDayStr.includes('ROUNDOF8')
      ) {
        return 20000
      }

      // Semi Finals (check before FINAL to avoid false match)
      if (matchDayStr.includes('SEMI FINAL') || matchDayStr.includes('SEMIFINAL')) {
        return 30000
      }

      // 3rd Place
      if (matchDayStr.includes('3RD') || matchDayStr.includes('THIRD')) {
        return 40000
      }

      // Final (must not be SEMI FINAL - already checked above)
      if (matchDayStr.includes('FINAL') && !matchDayStr.includes('SEMI')) {
        return 50000
      }

      // Any other matchday (not numeric, not special round)
      // Comes after numeric but before special rounds
      return 0
    }

    return [...matchdays].sort((a, b) => {
      const orderA = getSortOrder(a.matchday)
      const orderB = getSortOrder(b.matchday)

      // If both are negative (numeric matchdays), compare by absolute value for ascending order
      if (orderA < 0 && orderB < 0) {
        return Math.abs(orderA) - Math.abs(orderB)
      }

      return orderA - orderB
    })
  }

  useEffect(() => {
    if (matchday) {
      setMatches([])
      setWebsites([])
      setMatchdayData([])
      setMatchdayDataTotals([])
      setMatchdayDataSpectators(0)
      setHighlightWebsitesTotal(0)
      setLiveWebsitesTotal(0)
      fetchMatchDayData()
      fetchMatchDaysMatches()
      fetchMatchDayWebsites()
    }
  }, [matchday])

  useEffect(() => {
    fetchCompetitions()
  }, [fetchCompetitions])

  useEffect(() => {
    if (slctdCompetition) {
      fetchMatchDays()
    }
  }, [slctdCompetition])

  useEffect(() => {
    const url = new URL(window.location.href)
    const activeDay = url.searchParams.get('report')
    if (activeDay && matchdays.length && matchdays.some((md) => md.matchday === activeDay)) {
      setMatchday(activeDay)
    } else if (!activeDay) {
      // If no URL parameter, ensure no report is selected
      setMatchday('')
    }
  }, [matchdays])

  return (
    <AppLayout pageName={'Matchdays Report'}>
      {isLoading ? (
        <div className='flex justify-center w-full py-12'>
          <Spinner
            color='#CCCCCC'
            size={24}
          />
        </div>
      ) : (
        <>
          <div className='space-y-5'>
            <section>
              <div className='flex flex-wrap flex-col lg:flex-row justify-between'>
                <div className='inline-flex rounded-md mb-3'>
                  <CompetitionSelector
                    value={slctdCompetition}
                    competitions={competitions}
                    onChange={setslctdCompetition}
                  />

                  {/* {competitions.map((competition, index) => {
                    return (
                      <button
                        onClick={() => setslctdCompetition(competition)}
                        aria-current='page'
                        className={`px-4 py-2 text-sm font-medium tracking-wider ${
                          competition === slctdCompetition
                            ? 'text-white bg-main'
                            : 'text-neutral-800 bg-white border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        Ligue {competition}
                      </button>
                    )
                  })} */}
                </div>
              </div>

              <div className='space-y-3'>
                {sortMatchdays(matchdays).map((day, index) => {
                  return (
                    <section
                      className='!p-0'
                      key={index}
                    >
                      <div
                        className={`py-5 px-5 rounded-lg bg-white hover:bg-neutral-50 transition-all duration-200 cursor-pointer flex flex-row justify-between space-x-2 items-center ${
                          matchday === day.matchday ? 'border-b shadow-none bg-neutral-50' : ''
                        }`}
                        onClick={() => onClickMatchDay(day)}
                        style={{ alignItems: 'center' }}
                      >
                        <h5 className='text-base leading-none font-medium tracking-wide'>Day: {day.matchday}</h5>
                        <div
                          className='flex gap-4 items-center'
                          style={{ alignItems: 'center' }}
                        >
                          <button
                            className='btn-main'
                            onClick={(event) => {
                              event.stopPropagation()
                              setShowPdfDialog(true)
                            }}
                          >
                            <GrDownload />
                          </button>

                          <span
                            className={`transition-transform duration-200 ${
                              matchday === day.matchday ? 'rotate-90' : ''
                            }`}
                          >
                            <IoIosArrowForward />
                          </span>
                        </div>
                      </div>
                      {matchday === day.matchday && (
                        <div className='overflow-hidden transition-all duration-300 ease-out transform opacity-100 scale-100'>
                          {reportLoading ? (
                            <div className='flex justify-center w-full py-12'>
                              <Spinner
                                color='#CCCCCC'
                                size={24}
                              />
                            </div>
                          ) : (
                            <div
                              id={`report-${day.matchday}`}
                              className='space-y-10 px-5 py-7'
                            >
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-main text-3xl font-extrabold uppercase w-full'>
                                      PIRACY Live
                                    </div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-main py-4 print:shadow-none'>
                                    <table className='w-full text-sm text-center text-gray-800'>
                                      <thead className='text-xs text-neutral-800 uppercase light-bg'>
                                        <tr>
                                          <th
                                            colSpan={5}
                                            scope='col'
                                            className='px-6 pb-4 text-xl text-white bg-main capitalize'
                                          >
                                            Summary table for live links
                                          </th>
                                        </tr>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 py-4 text-left'
                                          >
                                            Platforms
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Number of live links
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Average closure time
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Closure rate
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Number of Spectators
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {websites
                                          .slice()
                                          .sort((a, b) => b.livelinks - a.livelinks)
                                          .map((website, index) => {
                                            return (
                                              <Fragment key={index}>
                                                {website.name !== 'Snapchat' && website.name !== 'DDL' && (
                                                  <tr className='bg-white border-b-4 border-blue-100'>
                                                    <th
                                                      scope='row'
                                                      className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                                    >
                                                      <div
                                                        className='flex space-x-2 items-center'
                                                        style={{ alignItems: 'center' }}
                                                      >
                                                        <img
                                                          width='20'
                                                          height='20'
                                                          src={`${process.env.REACT_APP_STATIC_URL || ''}${
                                                            website.icon
                                                          }`}
                                                        />
                                                        <div>
                                                          <h6 className='text-neutral-900 text-lg'>{website.name}</h6>
                                                          <h6 className='text-neutral-500 text-xs'>
                                                            {(website.name === 'Google' || website.name === 'Bing') &&
                                                              'delisting'}
                                                          </h6>
                                                        </div>
                                                      </div>
                                                    </th>
                                                    <td className='px-6 py-4 text-lg border-r-4 border-main'>
                                                      {website.name === 'Google' || website.name === 'Bing'
                                                        ? matchdayDataTotals.totalgooglelinks > 0
                                                          ? matchdayDataTotals.totalgooglelinks.toLocaleString()
                                                          : 0
                                                        : website.livelinks > 0
                                                        ? website.livelinks.toLocaleString()
                                                        : 0}
                                                    </td>
                                                    <td className='px-6 py-4 text-lg border-r-4 border-main'>
                                                      {website.time !== null && website.time}
                                                    </td>
                                                    <td className='px-6 py-4 text-lg border-r-4 border-main'>
                                                      {website.rate !== null && website.rate + '%'}
                                                    </td>
                                                    <td className='px-6 py-4 text-lg'>
                                                      {website.totalspectators === 0 || website.totalspectators === null
                                                        ? '-'
                                                        : Intl.NumberFormat('en-US')
                                                            .format(parseInt(website.totalspectators))
                                                            .replace(/,/g, ' ')}
                                                    </td>
                                                  </tr>
                                                )}
                                              </Fragment>
                                            )
                                          })}
                                      </tbody>
                                      <thead className='text-lg text-neutral-800 uppercase light-bg'>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 py-4 text-left'
                                          >
                                            TOTAL
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            {matchdayDataTotals.totallivelinks + matchdayDataTotals.totalgooglelinks > 0
                                              ? (
                                                  matchdayDataTotals.totallivelinks +
                                                  matchdayDataTotals.totalgooglelinks
                                                ).toLocaleString()
                                              : '-'}{' '}
                                            LINKS
                                          </th>
                                          <th></th>
                                          <th></th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            {matchdayDataSpectators > 0
                                              ? `${Intl.NumberFormat('en-US')
                                                  .format(parseInt(matchdayDataSpectators))
                                                  .replace(/,/g, ' ')} SPECTATORS`
                                              : '- SPECTATORS'}
                                          </th>
                                        </tr>
                                      </thead>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-main text-3xl font-extrabold uppercase'>PIRACY HIGHLIGHTS</div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-main py-4'>
                                    <table className='w-full text-sm text-center text-gray-800'>
                                      <thead className='text-xs text-neutral-800 uppercase light-bg'>
                                        <tr>
                                          <th
                                            colSpan={4}
                                            scope='col'
                                            className='px-6 pb-4 text-xl text-white bg-main capitalize'
                                          >
                                            Summary table for highlights links
                                          </th>
                                        </tr>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 py-4 text-left'
                                          >
                                            Platforms
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Number of notified highlights links
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Average closure time
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            Closure rate
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {websites
                                          .slice()
                                          .sort((a, b) => b.highlightlinks - a.highlightlinks)
                                          .map((website, index) => {
                                            return (
                                              <tr
                                                className='bg-white border-b-4 border-blue-100'
                                                key={index}
                                              >
                                                <th
                                                  scope='row'
                                                  className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                                >
                                                  <div
                                                    className='flex space-x-2 items-center'
                                                    style={{ alignItems: 'center' }}
                                                  >
                                                    <img
                                                      width='20'
                                                      height='20'
                                                      src={`${process.env.REACT_APP_STATIC_URL || ''}${website.icon}`}
                                                    />
                                                    <div>
                                                      <h6 className='text-neutral-900 text-lg'>{website.name}</h6>
                                                      <h6 className='text-neutral-500 text-xs'>
                                                        {(website.name === 'Google' || website.name === 'Bing') &&
                                                          'delisting'}
                                                      </h6>
                                                    </div>
                                                  </div>
                                                </th>
                                                <td className='px-6 py-4 text-lg border-r-4 border-main'>
                                                  {/* {((website.highlightlinks > 0) ? (website.highlightlinks).toLocaleString() : 0)} */}
                                                  {website.name === 'Google' || website.name === 'Bing'
                                                    ? matchdayDataTotals.totalhighlightlinks - highlightWebsitesTotal >
                                                      0
                                                      ? (
                                                          matchdayDataTotals.totalhighlightlinks -
                                                          highlightWebsitesTotal
                                                        ).toLocaleString()
                                                      : 0
                                                    : website.highlightlinks > 0
                                                    ? website.highlightlinks.toLocaleString()
                                                    : 0}
                                                </td>
                                                <td className='px-6 py-4 text-lg border-r-4 border-main'>
                                                  {website.highlighttime !== null && website.highlighttime}
                                                </td>
                                                <td className='px-6 py-4 text-lg'>
                                                  {website.highlightrate !== null && website.highlightrate + '%'}
                                                </td>
                                              </tr>
                                            )
                                          })}
                                      </tbody>
                                      <thead className='text-lg text-neutral-800 uppercase light-bg'>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 py-4 text-left'
                                          >
                                            TOTAL
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 py-4'
                                          >
                                            {matchdayDataTotals.totalhighlightlinks - ddlTotal > 0
                                              ? (matchdayDataTotals.totalhighlightlinks - ddlTotal).toLocaleString()
                                              : '-'}{' '}
                                            LINKS
                                          </th>
                                          <th></th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-xs leading-none text-black font-medium'>TOTAL</div>
                                    <div className='text-main text-3xl font-extrabold uppercase'>TOP EVENTS</div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-main py-4'>
                                    <table className='w-full text-sm text-center text-gray-800'>
                                      <thead className='text-xs text-white uppercase'>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4 text-left'
                                          >
                                            Event
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Date
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Total of links
                                          </th>
                                        </tr>
                                        <tr>
                                          <th
                                            colSpan={3}
                                            scope='col'
                                            className='px-6 py-3 text-left light-bg capitalize text-neutral-800'
                                          >
                                            Total (Live, Highlights, Google)
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {matches
                                          .slice()
                                          .sort((a, b) => b.totallinks - a.totallinks)
                                          .map((match, index) => (
                                            <tr
                                              className='bg-white border-b-4 border-blue-100'
                                              key={index}
                                            >
                                              <th
                                                scope='row'
                                                className='px-6 py-5 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                              >
                                                <Link
                                                  to={
                                                    isAdmin
                                                      ? `/match-details/${match.matchid}`
                                                      : `/matches/${match.competition}/${match.matchid}`
                                                  }
                                                  className='match-details-link'
                                                  title='open match details'
                                                >
                                                  <span>
                                                    {match.team1name} - {match.team2name}
                                                  </span>
                                                </Link>
                                              </th>
                                              <td className='px-6 py-5 border-r-4 border-main'>
                                                {format(match.date, 'dd/MM/yyyy')}
                                              </td>
                                              <td className='px-6 py-5'>
                                                {match.totallinks > 0 ? Number(match.totallinks).toLocaleString() : ''}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-xs leading-none text-black font-medium'>LIVE</div>
                                    <div className='text-main text-3xl font-extrabold uppercase'>TOP EVENTS</div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-main py-4'>
                                    <table className='w-full text-sm text-center text-gray-800'>
                                      <thead className='text-xs text-white uppercase'>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4 text-left'
                                          >
                                            Event
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Date
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Total of links
                                          </th>
                                        </tr>
                                        <tr>
                                          <th
                                            colSpan={3}
                                            scope='col'
                                            className='px-6 py-3 text-left light-bg capitalize text-neutral-800'
                                          >
                                            Live
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {matches
                                          .slice()
                                          .sort((a, b) => b.livelinks - a.livelinks)
                                          .map((match, index) => (
                                            <tr
                                              className='bg-white border-b-4 border-blue-100'
                                              key={index}
                                            >
                                              <th
                                                scope='row'
                                                className='px-6 py-5 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                              >
                                                <Link
                                                  to={
                                                    isAdmin
                                                      ? `/match-details/${match.matchid}`
                                                      : `/matches/${match.competition}/${match.matchid}`
                                                  }
                                                  className='match-details-link'
                                                  title='open match details'
                                                >
                                                  <span>
                                                    {match.team1name} - {match.team2name}
                                                  </span>
                                                </Link>
                                              </th>
                                              <td className='px-6 py-5 border-r-4 border-main'>
                                                {format(match.date, 'dd/MM/yyyy')}
                                              </td>
                                              <td className='px-6 py-5'>
                                                {match.livelinks > 0 ? match.livelinks.toLocaleString() : ''}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-xs leading-none text-black font-medium'>SPECTATORS</div>
                                    <div className='text-main text-3xl font-extrabold uppercase'>TOP EVENTS</div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-main py-4'>
                                    <table className='w-full text-sm text-center text-gray-800'>
                                      <thead className='text-xs text-white uppercase'>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4 text-left'
                                          >
                                            Event
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Date
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            NUMBER OF SPECTATORS
                                          </th>
                                        </tr>
                                        <tr>
                                          <th
                                            colSpan={3}
                                            scope='col'
                                            className='px-6 py-3 text-left light-bg capitalize text-neutral-800'
                                          >
                                            Spectators
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {matches
                                          .slice()
                                          .sort((a, b) => b.totalspectators - a.totalspectators)
                                          .map((match, index) => (
                                            <tr
                                              className='bg-white border-b-4 border-blue-100'
                                              key={index}
                                            >
                                              <th
                                                scope='row'
                                                className='px-6 py-5 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                              >
                                                <Link
                                                  to={
                                                    isAdmin
                                                      ? `/match-details/${match.matchid}`
                                                      : `/matches/${match.competition}/${match.matchid}`
                                                  }
                                                  className='match-details-link'
                                                  title='open match details'
                                                >
                                                  <span>
                                                    {match.team1name} - {match.team2name}
                                                  </span>
                                                </Link>
                                              </th>
                                              <td className='px-6 py-5 border-r-4 border-main'>
                                                {format(match.date, 'dd/MM/yyyy')}
                                              </td>
                                              <td className='px-6 py-5'>
                                                {match.totalspectators > 0
                                                  ? Number(match.totalspectators).toLocaleString()
                                                  : ''}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-xs leading-none text-black font-medium'>HIGHLIGHTS</div>
                                    <div className='text-main text-3xl font-extrabold uppercase'>TOP EVENTS</div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-main py-4'>
                                    <table className='w-full text-sm text-center text-gray-800'>
                                      <thead className='text-xs text-white uppercase bg-main'>
                                        <tr>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4 text-left'
                                          >
                                            Event
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Date
                                          </th>
                                          <th
                                            scope='col'
                                            className='px-6 pb-4'
                                          >
                                            Total of links
                                          </th>
                                        </tr>
                                        <tr>
                                          <th
                                            colSpan={3}
                                            scope='col'
                                            className='px-6 py-3 text-left light-bg capitalize text-neutral-800'
                                          >
                                            Highlights
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {matches
                                          .slice()
                                          .sort((a, b) => b.highlightlinks - a.highlightlinks)
                                          .map((match, index) => (
                                            <tr
                                              className='bg-white border-b-4 border-blue-100'
                                              key={index}
                                            >
                                              <th
                                                scope='row'
                                                className='px-6 py-5 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                              >
                                                <Link
                                                  to={
                                                    isAdmin
                                                      ? `/match-details/${match.matchid}`
                                                      : `/matches/${match.competition}/${match.matchid}`
                                                  }
                                                  className='match-details-link'
                                                  title='open match details'
                                                >
                                                  <span>
                                                    {match.team1name} - {match.team2name}
                                                  </span>
                                                </Link>
                                              </th>
                                              <td className='px-6 py-5 border-r-4 border-main'>
                                                {format(match.date, 'dd/MM/yyyy')}
                                              </td>
                                              <td className='px-6 py-5'>
                                                {match.highlightlinks > 0 ? match.highlightlinks.toLocaleString() : ''}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div>
                                  <div className='bg-white border rounded-lg p-6 pb-8'>
                                    <div className='text-main text-3xl font-extrabold uppercase'>
                                      NOTIFIED AND SUSPENDED ACCOUNTS
                                    </div>
                                    <div className='flex flex-wrap space-x-5 mt-3 ml-1 text-black text-lg'>
                                      <div
                                        className='flex space-x-2 items-center'
                                        style={{ alignItems: 'center' }}
                                      >
                                        <FaSquareXTwitter /> <span>X / Twitter</span>
                                      </div>
                                      <div
                                        className='flex space-x-2 items-center'
                                        style={{ alignItems: 'center' }}
                                      >
                                        <FaTiktok /> <span>Tiktok</span>
                                      </div>
                                      <div
                                        className='flex space-x-2 items-center'
                                        style={{ alignItems: 'center' }}
                                      >
                                        <FaTelegramPlane /> <span>Telegram</span>
                                      </div>
                                      <div
                                        className='flex space-x-2 items-center'
                                        style={{ alignItems: 'center' }}
                                      >
                                        <FaDiscord /> <span>Discord</span>
                                      </div>
                                      <div
                                        className='flex space-x-2 items-center'
                                        style={{ alignItems: 'center' }}
                                      >
                                        <FaSnapchatGhost /> <span>Snapchat</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 -mt-5 bg-white'>
                                    <div className='rounded-t-xl bg-main p-4'>
                                      <h4 className='text-white font-semibold text-2xl text-center'>
                                        Suspended Accounts
                                      </h4>
                                    </div>
                                    <div className='p-10x acc-table'>
                                      <table className='w-full text-sm text-center text-gray-800'>
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left w-1/2'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/x.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>X / Twitter</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accsuspendedtwitter > 0
                                              ? matchdayData.accsuspendedtwitter.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/tiktok.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>Tiktok</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accsuspendedtiktok > 0
                                              ? matchdayData.accsuspendedtiktok.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/telegram.png`}
                                              />
                                              <div>
                                                <h6 className='text-neutral-900 text-lg'>Telegram</h6>
                                                <h6 className='text-neutral-500 text-xs'>Suspended channels</h6>
                                              </div>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accsuspendedtelegram > 0
                                              ? matchdayData.accsuspendedtelegram.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                        <tr className='bg-white'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/discord.png`}
                                              />
                                              <div>
                                                <h6 className='text-neutral-900 text-lg'>Discord</h6>
                                                <h6 className='text-neutral-500 text-xs'>Suspended servers</h6>
                                              </div>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accsuspendeddiscord > 0
                                              ? matchdayData.accsuspendeddiscord.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                      </table>
                                    </div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 mt-3 bg-white'>
                                    <div className='rounded-t-xl bg-main p-4'>
                                      <h4 className='text-white font-semibold text-2xl text-center'>
                                        Notified Accounts
                                      </h4>
                                    </div>
                                    <div className='p-10x'>
                                      <table className='w-full text-sm text-center text-gray-800'>
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left w-1/2'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/x.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>X / Twitter</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accnotifiedtwitter > 0
                                              ? matchdayData.accnotifiedtwitter.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/tiktok.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>Tiktok</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accnotifiedtiktok > 0
                                              ? matchdayData.accnotifiedtiktok.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                        {/* <tr className="bg-white border-b-4 border-blue-100">
                                        <th scope="row" className="px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left">
                                          <div className="flex space-x-2 items-center">
                                              <img width="20" height="20" src={`/images/icons/telegram.png`} />
                                              <h6 className="text-neutral-900 text-lg">Telegram</h6>
                                          </div>
                                        </th>
                                        <td className="px-6 py-5 text-lg">{matchdayData && matchdayData.accnotifiedtelegram}</td>
                                    </tr> */}
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/snapchat.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>Snapchat</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accnotifiedsnapchat > 0
                                              ? matchdayData.accnotifiedsnapchat.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                      </table>
                                    </div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 mt-3 bg-white'>
                                    <div className='rounded-t-xl bg-main p-4'>
                                      <h4 className='text-white font-semibold text-2xl text-center'>
                                        Channels Notified
                                      </h4>
                                    </div>
                                    <div className='p-10x'>
                                      <table className='w-full text-sm text-center text-gray-800'>
                                        <tr className='bg-white border-b-4 border-blue-100'>
                                          <th
                                            scope='row'
                                            className='px-6 py-6 font-medium border-r-4 border-main whitespace-nowrap text-left w-1/2'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/telegram.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>Telegram</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.accnotifiedtelegram > 0
                                              ? matchdayData.accnotifiedtelegram.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                      </table>
                                    </div>
                                  </div>
                                  <div className='rounded-xl border shadow mx-5 mt-3 bg-white'>
                                    <div className='rounded-t-xl bg-main p-4'>
                                      <h4 className='text-white font-semibold text-2xl text-center'>
                                        Impacted Members
                                      </h4>
                                    </div>
                                    <div className='p-10x'>
                                      <table className='w-full text-sm text-center text-gray-800'>
                                        <tr className='bg-white'>
                                          <th
                                            scope='row'
                                            className='px-6 py-4 font-medium border-r-4 border-main whitespace-nowrap text-left w-1/2'
                                          >
                                            <div
                                              className='flex space-x-2 items-center'
                                              style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                              <img
                                                width='20'
                                                height='20'
                                                src={`/images/icons/telegram.png`}
                                              />
                                              <h6 className='text-neutral-900 text-lg'>Telegram</h6>
                                            </div>
                                          </th>
                                          <td className='px-6 py-5 text-lg'>
                                            {matchdayData && matchdayData.membersinspactedtelegram > 0
                                              ? matchdayData.membersinspactedtelegram.toLocaleString()
                                              : ''}
                                          </td>
                                        </tr>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                  )
                })}
              </div>
            </section>
          </div>
        </>
      )}
      <ReportPdfDialog
        isOpen={showPdfDialog}
        onClose={() => setShowPdfDialog(false)}
        data={{
          websites,
          matches,
          matchdayData,
          matchdayDataTotals,
          matchdayDataSpectators,
          highlightWebsitesTotal,
          ddlTotal,
          matchday,
          competitionName: slctdCompetition?.name || '',
        }}
      />
    </AppLayout>
  )
}

export default Report
